import React, { useState, useEffect } from "react";
import { useCountryCodeQuery } from "../../Store/Apis/customerApi";
import { useSelector } from "react-redux";

const AddEditCustomerForm = ({ customerData, handleSubmit, isNew = false }) => {
  const user = useSelector((state) => state.auth.user);
  const [formData, setFormData] = useState({
    // Fields kept for backwards compatibility when editing
    salutation: customerData?.salutation || "",
    customerName: customerData?.customerName || "",
    dateOfBirth: customerData?.dateOfBirth || "",
    countryCode: customerData?.countryCode || 27,
    phoneNumber: customerData?.phoneNumber || "",
    email: customerData?.email || "",
    taxNumber: customerData?.taxNumber || "",
    customerRating: customerData?.customerRating || "",
    locality: customerData?.locality || "",
    pincode: customerData?.pincode || "",
    address: customerData?.address || "",
    clientType: customerData?.clientType || "individual", // new toggle (individual | company)
  });

  const [errors, setErrors] = useState({});

  const countryCodes = useCountryCodeQuery();

  useEffect(() => {
    if (customerData) {
      setFormData(customerData);
    }
  }, [customerData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = `${
        formData.clientType === "company" ? "Company" : "Customer"
      } Name is required.`;
    } else if (formData.customerName.length < 3) {
      newErrors.customerName = `${
        formData.clientType === "company" ? "Company" : "Customer"
      } Name must be at least 3 characters long.`;
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required.";
    } else if (!/^\d{9}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 9 digits.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email format is invalid.";
    }

    return newErrors;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length === 0) {
      handleSubmit({
        ...formData,
        outletID: user.outletID,
      }); // Call parent’s submit handler
    } else {
      setErrors(validationErrors);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="row gy-3 gx-2">
        {isNew && (
          <div className="col-12">
            <div className="d-flex gap-3 align-items-center">
              <label className="mb-0">Client Type:</label>
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    formData.clientType === "individual"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() =>
                    setFormData({ ...formData, clientType: "individual" })
                  }
                >
                  Individual
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    formData.clientType === "company"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() =>
                    setFormData({ ...formData, clientType: "company" })
                  }
                >
                  Company
                </button>
              </div>
            </div>
          </div>
        )}
        {!isNew && formData.clientType === "individual" && (
          <div className="col-md-6">
            <div>
              <label>Salutation</label>
              <select
                className={`form-control`}
                name="salutation"
                value={formData.salutation}
                onChange={handleInputChange}
              >
                <option value="">Select</option>
                <option value="Mr">Mr</option>
                <option value="Ms">Ms</option>
                <option value="Mrs">Mrs</option>
                <option value="Miss">Miss.</option>
                <option value="Dr">Dr.</option>
              </select>
            </div>
          </div>
        )}
        <div
          className={
            !isNew && formData.clientType === "individual"
              ? "col-md-6"
              : "col-md-12"
          }
        >
          <div>
            <label>
              {formData.clientType === "company"
                ? "Company Name*"
                : "Customer Name*"}
            </label>
            <input
              type="text"
              className={`form-control ${
                errors.customerName ? "is-invalid" : ""
              }`}
              name="customerName"
              value={formData.customerName}
              onChange={handleInputChange}
            />
            {errors.customerName && (
              <div className="invalid-feedback">{errors.customerName}</div>
            )}
          </div>
        </div>
        {!isNew && formData.clientType === "individual" && (
          <div className="col-md-12">
            <div>
              <label>Date of Birth</label>
              <input
                type="date"
                className="form-control"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
              />
            </div>
          </div>
        )}
        <div className="col-md-6">
          <div>
            <label>Country Code</label>
            <select
              className="form-control"
              name="countryCode"
              value={formData.countryCode}
              onChange={handleInputChange}
              defaultValue={27}
            >
              <option value="">Select</option>
              {countryCodes?.data?.data.map((elem, i) => {
                return (
                  <option value={elem.phoneCode}>
                    {elem.countryCode} ({elem.phoneCode})
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className="col-md-6">
          <div>
            <label>Phone Number*</label>
            <input
              type="text"
              className={`form-control ${
                errors.phoneNumber ? "is-invalid" : ""
              }`}
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
            />
            {errors.phoneNumber && (
              <div className="invalid-feedback">{errors.phoneNumber}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div>
            <label>Email</label>
            <input
              type="email"
              className={`form-control ${errors.email ? "is-invalid" : ""}`}
              name="email"
              value={formData.email}
              onChange={handleInputChange}
            />
            {errors.email && (
              <div className="invalid-feedback">{errors.email}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div>
            <label>Tax Number</label>
            <input
              type="text"
              className="form-control"
              name="taxNumber"
              value={formData.taxNumber}
              onChange={handleInputChange}
            />
          </div>
        </div>
        {!isNew && formData.clientType === "individual" && (
          <>
            <div className="col-md-12">
              <div>
                <label>Customer Rating</label>
                <select
                  className={`form-control`}
                  name="customerRating"
                  value={formData.customerRating}
                  onChange={handleInputChange}
                >
                  <option value="">Select</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                </select>
                {errors.customerRating && (
                  <div className="invalid-feedback">
                    {errors.customerRating}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div>
                <label>Locality</label>
                <input
                  type="text"
                  className="form-control"
                  name="locality"
                  value={formData.locality}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div>
                <label>Pincode</label>
                <input
                  type="text"
                  className="form-control"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </>
        )}
        <div className="col-md-12">
          <div>
            <label>Address</label>
            <textarea
              className="form-control"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
            />
          </div>
        </div>
        {/* Status toggle removed to avoid unintended activation/deactivation during edit */}
        <div className="col-12">
          <div className="text-center">
            <button type="submit" className="btn btn-primary">
              {customerData
                ? `Update ${
                    formData.clientType === "company" ? "Company" : "Customer"
                  }`
                : `Add New ${
                    formData.clientType === "company" ? "Company" : "Client"
                  }`}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AddEditCustomerForm;
